/**
 * EditPractitionerModal Component
 *
 * Modal form for editing an existing practitioner's details.
 * Uses the same styling as OnboardingFormModal.
 */

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/base';
import { onboardingApi } from '@/lib/api/onboarding';
import { createComponentLogger } from '@/lib/utils/logger';
import type { RoleOption } from '@/lib/types/onboarding';
import { DEFAULT_ROLE_OPTIONS } from '@/lib/types/onboarding';
import { useSpecialties } from '@/lib/hooks/useSpecialties';
import { AdditionalSpecialtiesInput } from '@/components/forms/AdditionalSpecialtiesInput';
import { SpecialtyPicker } from '@/components/forms/SpecialtySearch';
import { INPUT_CLASS, LABEL_CLASS, ERROR_CLASS } from '@/components/forms/formStyles';
import { sanitizePhoneInput } from '@/lib/utils/phone';

const logger = createComponentLogger('EditPractitionerModal');

const editSchema = z
  .object({
    firstName: z
      .string()
      .min(2, 'First name must be at least 2 characters')
      .max(50, 'First name must not exceed 50 characters')
      .refine((val) => /^[a-zA-Z\s]+$/.test(val), {
        message: 'First name can only contain letters and spaces',
      }),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(50, 'Last name must not exceed 50 characters')
      .refine((val) => /^[a-zA-Z\s]+$/.test(val), {
        message: 'Last name can only contain letters and spaces',
      }),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
    phone: z
      .string()
      .optional()
      .refine((val) => {
        if (!val) return true;
        return /^\d{10}$/.test(val);
      }, 'Phone number must be exactly 10 digits'),
    role: z.string().min(1, 'Please select a role'),
    specialty: z.string().optional(),
    active: z.boolean(),
  })
  .refine((data) => data.role !== 'practitioner' || !!data.specialty, {
    message: 'Specialty is required for practitioners',
    path: ['specialty'],
  });

export interface EditPractitionerData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string | null;
  specialty?: string;
  /** Specialties after the first, as their display labels */
  additionalSpecialties?: string[];
  active: boolean;
}

export interface EditPractitionerModalProps {
  isOpen: boolean;
  onClose: () => void;
  practitioner: EditPractitionerData | null;
}

export function EditPractitionerModal({
  isOpen,
  onClose,
  practitioner,
}: EditPractitionerModalProps) {
  const queryClient = useQueryClient();

  const { data: roles = DEFAULT_ROLE_OPTIONS } = useQuery({
    queryKey: ['onboarding-roles'],
    queryFn: () => onboardingApi.fetchRoles(),
    staleTime: 10 * 60 * 1000,
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(editSchema) as never,
    mode: 'onBlur',
    values: practitioner
      ? {
          firstName: practitioner.firstName,
          lastName: practitioner.lastName,
          email: practitioner.email,
          phone: sanitizePhoneInput(practitioner.phone),
          role: practitioner.role || '',
          specialty: practitioner.specialty || '',
          active: practitioner.active,
        }
      : undefined,
  });

  const selectedRole = watch('role');
  const isPractitionerRole = selectedRole === 'practitioner';
  const {
    data: specialties = [],
    isLoading: isLoadingSpecialties,
    error: specialtiesError,
  } = useSpecialties();
  const specialtiesStatus = isLoadingSpecialties
    ? 'Loading specialties…'
    : specialtiesError
      ? specialtiesError.message
      : undefined;
  const [additionalSpecialties, setAdditionalSpecialties] = useState<string[]>([]);

  // Reset form when practitioner changes
  useEffect(() => {
    if (practitioner) {
      setAdditionalSpecialties(practitioner.additionalSpecialties ?? []);
      reset({
        firstName: practitioner.firstName,
        lastName: practitioner.lastName,
        email: practitioner.email,
        phone: sanitizePhoneInput(practitioner.phone),
        role: practitioner.role || '',
        specialty: practitioner.specialty || '',
        active: practitioner.active,
      });
    }
  }, [practitioner, reset]);

  const updateMutation = useMutation({
    mutationFn: async (values: z.infer<typeof editSchema>) => {
      const result = await onboardingApi.updateUser(practitioner!.id, {
        email: values.email.trim(),
        role: values.role,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        ...(isPractitionerRole && values.specialty
          ? { specialty: [values.specialty, ...additionalSpecialties] }
          : {}),
        ...(values.phone ? { phone: values.phone } : {}),
      });

      if (values.active !== practitioner!.active) {
        if (values.active) {
          await onboardingApi.activateUser(practitioner!.id);
        } else {
          await onboardingApi.deactivateUser(practitioner!.id);
        }
      }

      return result;
    },
    onSuccess: (data) => {
      logger.log('Update successful:', data);
      toast.success(data.message || 'Practitioner updated successfully');
      queryClient.invalidateQueries({ queryKey: ['practitioners'] });
      queryClient.invalidateQueries({ queryKey: ['practitioner-roles'] });
      reset();
      onClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update practitioner';
      logger.error('Update error:', message);
      toast.error(message);
    },
  });

  const onSubmit = handleSubmit((values) => {
    updateMutation.mutate(values);
  });

  const handleClose = () => {
    if (isSubmitting || updateMutation.isPending) return;
    reset();
    onClose();
  };

  if (!practitioner) return null;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      size="lg"
      hideCloseButton={false}
      disableOutsideClick={true}
    >
      <div
        className="max-h-[80vh] overflow-y-auto pr-2"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
      >
        <div className="mb-3 text-center">
          <h2 className="font-hanken text-lg font-medium leading-overview-title tracking-normal text-gray-700">
            Edit Practitioner
          </h2>
          <p className="mx-auto mt-1 w-3/4 font-hanken text-sm font-normal leading-label-2 tracking-label-2 text-gray-700">
            Update the practitioner's details and access configuration.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-2.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                First Name <span className="ml-1 text-destructive">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter first name"
                className={INPUT_CLASS}
                {...register('firstName')}
                disabled={isSubmitting}
              />
              {errors.firstName && <p className={ERROR_CLASS}>{errors.firstName.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Last Name <span className="ml-1 text-destructive">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter last name"
                className={INPUT_CLASS}
                {...register('lastName')}
                disabled={isSubmitting}
              />
              {errors.lastName && <p className={ERROR_CLASS}>{errors.lastName.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                Email <span className="ml-1 text-destructive">*</span>
              </label>
              <input
                type="email"
                placeholder="user@hospital.com"
                className={INPUT_CLASS}
                {...register('email')}
                disabled={isSubmitting}
              />
              {errors.email && <p className={ERROR_CLASS}>{errors.email.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>Phone (optional)</label>
              <input
                type="tel"
                placeholder="10-digit phone"
                className={INPUT_CLASS}
                {...register('phone')}
                disabled={isSubmitting}
                maxLength={10}
              />
              {errors.phone && <p className={ERROR_CLASS}>{errors.phone.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                Role <span className="ml-1 text-destructive">*</span>
              </label>
              <select
                className={`${INPUT_CLASS} appearance-none`}
                {...register('role')}
                disabled={isSubmitting}
              >
                {roles.map((role: RoleOption) => (
                  <option key={role.code} value={role.code}>
                    {role.display}
                  </option>
                ))}
              </select>
              {errors.role && <p className={ERROR_CLASS}>{errors.role.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Specialty
                {isPractitionerRole ? (
                  <span className="ml-0.5 text-destructive">*</span>
                ) : (
                  <span className="ml-1 text-xs text-gray-400">(practitioners only)</span>
                )}
              </label>
              <Controller
                control={control}
                name="specialty"
                render={({ field }) => (
                  <SpecialtyPicker
                    specialties={specialties}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isSubmitting || !isPractitionerRole}
                    invalid={!!errors.specialty}
                    statusMessage={specialtiesStatus}
                  />
                )}
              />
              {errors.specialty && <p className={ERROR_CLASS}>{errors.specialty.message}</p>}
            </div>
          </div>

          {isPractitionerRole && (
            <div>
              <label className={LABEL_CLASS}>
                Additional specialties
                <span className="ml-1 text-xs text-gray-400">(optional)</span>
              </label>
              <AdditionalSpecialtiesInput
                value={additionalSpecialties}
                onChange={setAdditionalSpecialties}
                specialties={specialties}
                disabled={isSubmitting}
                statusMessage={specialtiesStatus}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="h-9 rounded-md border border-gray-300 bg-white px-6 font-hanken text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex h-9 items-center gap-2 rounded-md bg-[#061a61] px-6 font-hanken text-sm font-medium text-white transition-colors hover:bg-[#0a2480] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting ? 'Updating...' : 'Update Practitioner'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default EditPractitionerModal;
